import {
	action,
	computed,
	makeObservable,
	observable,
	runInAction,
	toJS,
} from 'mobx';
import React from 'react';
import type {ModalWrapperProps} from './ModalWrapper';
import {getPrefixedLogger} from '@netx/core-next/instance/logger';

const logger = getPrefixedLogger('ModalManagerStore');

// TODO: we should convert this over to zustand - al areas using mobx can be reqritten with zustand

type TModalManagerItem<C extends React.ElementType = React.ElementType> = {
	id: string;
	Component: C;
	props: JSX.IntrinsicAttributes &
		JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>;
	open: boolean;
};

export class ModalManagerItem<C extends React.ElementType = React.ElementType>
	implements TModalManagerItem<C>
{
	Component: C;

	id: string;
	open = false;
	props = observable.object<
		JSX.IntrinsicAttributes &
			JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>
	>({} as any, {deep: true});
	shown = false;
	showing = false;
	hiding = false;

	resolveShown: () => void = () => {};
	rejectShown: (reason?: any) => void = () => {};

	resolveHidden: () => void = () => {};
	rejectHidden: (reason?: any) => void = () => {};

	get rawProps() {
		return toJS(this.props);
	}

	constructor(params: TModalManagerItem<C>) {
		this.id = params.id;
		this.Component = params.Component;

		makeObservable(this, {
			hide: action,
			hiding: observable,
			open: observable,
			props: observable,
			set: action,
			show: action,
			rawProps: computed,
			shown: observable,
			showing: observable,
		});

		runInAction(() => {
			Object.assign(this.props, params.props);
			this.id = params.id;
			this.Component = params.Component;
			// this.open = false;
		});
	}

	async hide() {
		logger.debug('item >', 'hide', this.id);
		this.open = false;
		this.showing = false;
		this.hiding = true;
		return new Promise<void>((resolve, reject) => {
			this.resolveHidden = () => {
				logger.debug('item >', 'resolving', this.id);
				runInAction(() => {
					this.hiding = false;
					this.shown = false;
				});
				return resolve();
			};
			this.rejectHidden = (reason?: any) => {
				logger.debug('item >', 'rejecting', this.id, 'reason', reason);
				reject(reason);
			};
		});
	}

	async show() {
		if (this.open) return;
		this.open = true;
		this.showing = true;
		this.hiding = false;
		return new Promise<void>((resolve, reject) => {
			this.resolveShown = () => {
				runInAction(() => {
					this.showing = false;
					this.shown = true;
				});
				return resolve();
			};
			this.rejectShown = (reason?: any) => reject(reason);
		});
	}

	set(
		props: JSX.IntrinsicAttributes &
			JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
	) {
		this.props = props;
	}
}

export class ModalManagerStore {
	items = observable.array<ModalManagerItem>([], {deep: true});

	constructor() {
		makeObservable(this, {
			items: observable,
			hide: action,
			show: action,
		});
	}

	async hide(id: string) {
		logger.debug('hide >', 'looking for existing modal', id);
		const existing = this.items.find((item) => item.id === id);
		if (!existing) {
			logger.debug('hide >', 'could not find existing modal', id);
			return;
		}
		logger.debug('hide >', 'found existing modal', id);
		return existing.hide().then(() => {
			logger.debug('hide >', 'removing from items', id);
			this.items.splice(this.items.indexOf(existing), 1);
			// action(() => {
			// 	this.items.splice(this.items.indexOf(existing), 1);
			// });
		});
	}

	resolveHidden(id: string) {
		const existing = this.items.find((item) => item.id === id);
		if (!existing) return;
		existing.resolveHidden();
	}

	resolveShown(id: string) {
		const existing = this.items.find((item) => item.id === id);
		if (!existing) return;
		existing.resolveShown();
	}

	rejectHidden(id: string, reason?: string) {
		const existing = this.items.find((item) => item.id === id);
		if (!existing) return;
		existing.rejectHidden(reason);
	}

	rejectShown(id: string, reason?: string) {
		const existing = this.items.find((item) => item.id === id);
		if (!existing) return;
		existing.rejectShown(reason);
	}

	async show<C extends React.ElementType>(
		id: string,
		Component: C,
		props: Omit<
			JSX.IntrinsicAttributes &
				JSX.LibraryManagedAttributes<C, React.ComponentProps<C>>,
			'open' | 'onClose' | 'DialogProps'
		> &
			Partial<ModalWrapperProps>,
	) {
		const existing = this.items.find((item) => item.id === id);
		if (existing) {
			existing.set(props);
			return existing.show();
		}

		const item = new ModalManagerItem<React.ElementType>({
			id,
			Component,
			props,
			open: false,
		});
		this.items.push(item);

		return item.show();
	}

	dispose() {
		this.items.replace([]);
	}
}

export const modalManagerStore = new ModalManagerStore();

export default modalManagerStore;
